import { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { AiControlSystem, AiControlSystemKey, AiEditableSystemKey } from "../api/types";
import { AiDraftTest } from "../components/aiControl/AiDraftTest";
import { AiInstructionPartView } from "../components/aiControl/AiInstructionPartView";
import { AiInstructionsEditor } from "../components/aiControl/AiInstructionsEditor";
import { AiInstructionVersionsList } from "../components/aiControl/AiInstructionVersionsList";
import { fmtAiWhen } from "../components/aiControl/aiControlUi";
import { SegmentedControl } from "../components/ui/SegmentedControl";

const SYSTEM_NAMES: Record<AiControlSystemKey, string> = {
  ITEM_PHOTO_SEARCH: "Photo search",
  BLOG_POST_WRITE: "Blog writer",
  TELEGRAM_ASSISTANT: "Telegram assistant",
};

const EDITABLE: ReadonlySet<AiControlSystemKey> = new Set<AiEditableSystemKey>(["ITEM_PHOTO_SEARCH"]);

type View = "instructions" | "versions" | "test";

/**
 * G-149 (OD-230) — AI control: what each AI system is told. Owners and
 * admins only; managers never see the page. Locked parts are the contract
 * the code depends on; the photo search's editable parts can be changed
 * here (S2): draft, test on past kiosk searches, publish, roll back.
 */
export function AiControlPage() {
  const [systems, setSystems] = useState<AiControlSystem[] | null>(null);
  const [selected, setSelected] = useState<AiControlSystemKey>("ITEM_PHOTO_SEARCH");
  const [view, setView] = useState<View>("instructions");
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .aiControlSystems()
      .then((list) => {
        if (!cancelled) setSystems(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load AI control.");
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const current = systems?.find((s) => s.key === selected) ?? null;
  const editing = current && EDITABLE.has(current.key) ? current.editing : null;
  const editableKey = editing ? (current?.key as AiEditableSystemKey) : null;

  /** After a save, a test, a publish or a roll back: the parts' live text and the pills come with a reload. */
  const reload = () => setReloadTick((t) => t + 1);

  function choose(key: AiControlSystemKey) {
    setSelected(key);
    setView("instructions");
  }

  return (
    <div className="ai-control">
      <header className="page-header">
        <h1>AI control</h1>
      </header>

      {error ? <div className="alert error">{error}</div> : null}
      {!systems && !error ? <p className="muted">Loading…</p> : null}

      {systems ? (
        <div className="ai-control-systems" role="tablist" aria-label="AI systems">
          {systems.map((system) => (
            <button
              key={system.key}
              type="button"
              role="tab"
              aria-selected={system.key === selected}
              className={`ai-control-system${system.key === selected ? " is-selected" : ""}`}
              onClick={() => choose(system.key)}
            >
              <strong>{SYSTEM_NAMES[system.key]}</strong>
              <span className="ai-control-models">
                {system.models.length > 0 ? system.models.join(" · ") : "—"}
              </span>
              <span className="ai-control-pills">
                <StatePill system={system} />
                {system.editing?.draft ? <span className="ai-control-pill is-warning">Draft</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {current && editing && editableKey ? (
        <>
          {view !== "test" ? (
            <div className="ai-control-viewbar">
              <SegmentedControl<View>
                ariaLabel="View"
                options={[
                  { value: "instructions", label: "Instructions" },
                  { value: "versions", label: "Versions" },
                ]}
                value={view}
                onChange={setView}
              />
              <span className="muted">
                {editing.live.number === 0
                  ? "Text in code"
                  : `Live since ${fmtAiWhen(editing.live.since)}${editing.live.by ? ` · ${editing.live.by}` : ""}`}
              </span>
            </div>
          ) : null}
          {view === "instructions" ? (
            <AiInstructionsEditor
              systemKey={editableKey}
              system={current}
              editing={editing}
              onChanged={reload}
              onTest={() => setView("test")}
            />
          ) : view === "versions" ? (
            <AiInstructionVersionsList
              systemKey={editableKey}
              system={current}
              editing={editing}
              onChanged={reload}
              onTest={() => setView("test")}
            />
          ) : (
            <AiDraftTest
              systemKey={editableKey}
              editing={editing}
              onBack={() => setView("instructions")}
              onChanged={reload}
            />
          )}
        </>
      ) : current && current.parts.length > 0 ? (
        <section className="ai-control-parts" aria-label={`${SYSTEM_NAMES[current.key]} instructions`}>
          {current.parts.map((part, index) => (
            <AiInstructionPartView key={`${part.title ?? ""}-${index}`} part={part} startOpen={index === 0} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function StatePill({ system }: { system: AiControlSystem }) {
  if (system.state === "NOT_CONNECTED") {
    return <span className="ai-control-pill is-muted">Not connected</span>;
  }
  if (system.state === "NOT_SET_UP") {
    return <span className="ai-control-pill is-warning">Not set up</span>;
  }
  return <span className="ai-control-pill is-live">Live{system.version ? ` ${system.version}` : ""}</span>;
}
